// Projectkaart: dezelfde gezondheidsinformatie als de tabel, maar met een
// periodebalk op eigen schaal (start → nu → oplevering) en het team.
// Rustig: neutraal, alleen het signaal kleurt (rood/oranje) bij een probleem.
import React from 'react';
import { Box, Typography, Avatar, AvatarGroup } from '@mui/material';
import { StoplichtRij } from '../../../ui-components/ProjectStoplicht';
import {
  T, Icon, Tip, Signaal, PeriodeBalk, UrenMeter, TakenTeller, VolgendeDeadline,
  PlAvatar, FaseChip, getInitials, PlanningWaarschuwing, projectHref, opGewoneKlik, linkSx,
} from './onderdelen';
import { korteDatum, relatief, vandaagDag } from './projectGezondheid';

const Label = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline, mb: 0.25 }}>{children}</Typography>
);

export default function ProjectKaart({ rij, stoplicht, onOpen }) {
  const g = rij.gezondheid;
  const vandaag = vandaagDag();
  const overigTeam = rij.team.filter((n) => !rij.pls.includes(n));
  return (
    <Box component="a" href={projectHref(rij.id)} onClick={(e) => opGewoneKlik(e, () => onOpen(rij))}
      sx={{ ...linkSx, bgcolor: T.surface, borderRadius: '0.75rem', p: 2, cursor: 'pointer',
        minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25,
        transition: 'box-shadow 0.15s', '&:hover': { boxShadow: '0 8px 24px rgba(45,51,53,0.08)' },
        '&:focus-visible': { outline: `2px solid ${T.primary}` } }}>
      {/* kop: status · nummer · fase */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {!rij.isLead && <Box sx={{ minWidth: 0, flex: 1 }}><Signaal gezondheid={g} metStatus /></Box>}
        {rij.isLead && <Box sx={{ flex: 1 }} />}
        <Box sx={{ flexShrink: 0, maxWidth: '45%' }}><FaseChip fase={rij.fase} /></Box>
        {rij.nummer && <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: T.klein, color: T.outline }}>{rij.nummer}</Typography>}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: T.groot, fontWeight: 600, color: T.text, lineHeight: 1.3,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{rij.naam}<PlanningWaarschuwing planning={rij.planning} /></Typography>
        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline, mt: 0.25 }}>
          {[rij.klant, rij.type].filter(Boolean).join(' · ') || ' '}
        </Typography>
      </Box>

      {!rij.isLead && (
        <>
          <Box>
            <PeriodeBalk gezondheid={g} />
            {(g?.start || g?.eind) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline }}>{g.start ? korteDatum(g.start, vandaag) : 'start ?'}</Typography>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline }}>
                  {g.eind ? `${korteDatum(g.eind, vandaag)} · ${relatief(g.eindOver)}` : 'oplevering ?'}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 1.25, alignItems: 'start' }}>
            <Box sx={{ minWidth: 0 }}><Label>Volgende</Label><VolgendeDeadline gezondheid={g} /></Box>
            <Box sx={{ minWidth: 0 }}><Label>Taken</Label><TakenTeller gezondheid={g} fallbackTotaal={rij.takenTotaal} /></Box>
            <Box sx={{ minWidth: 0 }}><Label>Uren</Label><UrenMeter uren={g?.uren} compact onClick={() => onOpen(rij, 'budget')} /></Box>
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', minWidth: 0 }}>
        <PlAvatar namen={rij.pls} avatar />
        {overigTeam.length > 0 && (
          <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 20, height: 20, fontSize: T.klein, fontWeight: 600, border: '2px solid #fff', bgcolor: T.surfaceHigh, color: T.secondary } }}>
            {overigTeam.map((n) => (
              <Tip key={n} title={n}><Avatar>{getInitials(n)}</Avatar></Tip>
            ))}
          </AvatarGroup>
        )}
        <Box sx={{ flex: 1 }} />
        {rij.notities > 0 && (
          <Tip title="Notities & gekoppelde meetings">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Icon name="speaker_notes" size={13} sx={{ color: T.outlineVariant }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline }}>{rij.notities}</Typography>
            </Box>
          </Tip>
        )}
        {!rij.isLead && <StoplichtRij status={stoplicht} size={8} />}
      </Box>
    </Box>
  );
}
